import { type FC, type ReactNode } from 'react';

/**
 * Column widths as proportions, not the frame's 361/322/429 pixels. The frame
 * lays those out inside a 1112px container and the shell's content column is
 * 1136 wide (see content-column.ts), so the pixel values reproduce neither one —
 * and three fixed columns overflow the card they sit in on a narrow window. The
 * ratios are the frame's, to one decimal place.
 *
 * The third column takes whatever is left, so it carries no width of its own.
 */
const COLUMN_TITLE = 'w-[32.5%]';
const COLUMN_BADGE = 'w-[29%]';

/**
 * Cell padding, per the frame: 24 outside the table's left and right edges, 16
 * between columns, 8 above and below.
 *
 * The frame's header row trails its second column by 32 and its third by 24,
 * while the body rows trail them by 24 and 32. Nothing in the layout turns on
 * the swap and the columns have to line up, so both rows use one set.
 */
const CELL_TITLE = 'py-2 pr-8 pl-6';
const CELL_BADGE = 'py-2 pr-8 pl-4';
const CELL_TRAILING = 'py-2 pr-6 pl-4';

/**
 * 14/20 weight 500 — the frame's `paragraph small/medium`.
 *
 * text-secondary is `raw-stone-600` and not the token of that name: the frame's
 * text-secondary is #57534E and the exported --ds-color-text-secondary is
 * #78716C. The same drift empty-state.tsx and project-overview-tabs.tsx
 * document; the raw step is what renders the frame's colour.
 */
const HEADER_TEXT =
  'text-left text-[14px] leading-[20px] font-medium text-[var(--ds-color-raw-stone-600)]';

/** 14/20 regular, the frame's `paragraph small/regular`. */
const TRAILING_TEXT = 'text-[14px] leading-[20px] text-[var(--ds-color-raw-stone-600)]';

/** Placeholder fill and pulse, as project-list-loading.tsx sets them. */
const FILL = 'bg-[var(--ds-color-bg-tertiary)]';
const PULSE = 'motion-safe:animate-pulse';

/** Enough rows to fill the eye without pretending to know the real count. */
const PLACEHOLDER_COUNT = 6;

interface PeopleTableProps {
  /**
   * Names the table for a screen reader. The sub-tab control above it is a set
   * of buttons rather than tabs, so nothing else says which of the two lists
   * this is.
   */
  caption: string;
  /** The three column headers, in the frame's order. */
  headers: readonly [string, string, string];
  children: ReactNode;
}

/**
 * The bordered card and three-column grid both people lists share.
 *
 * A real <table> rather than the frame's divs: these are rows of data about a
 * set of people, and only a table gives a screen reader the row-to-header
 * association that makes the middle column mean anything. The frame's geometry
 * survives it — `table-fixed` with the widths above is what the frame's fixed
 * cells were expressing.
 */
export const PeopleTable: FC<PeopleTableProps> = ({ caption, headers, children }) => (
  // overflow-hidden clips the header fill to the 8px radius.
  <div className="overflow-hidden rounded-[var(--ds-radius-8)] border border-[var(--ds-color-border-primary)]">
    <table className="w-full table-fixed">
      <caption className="sr-only">{caption}</caption>
      <thead className="bg-[var(--ds-color-bg-secondary)]">
        <tr className="border-b border-[var(--ds-color-border-primary)]">
          <th className={`${COLUMN_TITLE} ${CELL_TITLE} ${HEADER_TEXT}`} scope="col">
            {headers[0]}
          </th>
          <th className={`${COLUMN_BADGE} ${CELL_BADGE} ${HEADER_TEXT}`} scope="col">
            {headers[1]}
          </th>
          <th className={`${CELL_TRAILING} ${HEADER_TEXT}`} scope="col">
            {headers[2]}
          </th>
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

interface PeopleRowProps {
  /** The first line of the name cell. */
  title: string;
  /** The line under it. Invites carry an email and nothing else, so optional. */
  subtitle?: string;
  badge: ReactNode;
  /** The third column. A node, because it is a date or a stand-in for one. */
  trailing: ReactNode;
}

/**
 * One row.
 *
 * No fixed height: the frame's 55px is 8 + 20 + 18 + 8 plus the rule, so the
 * padding and the two line boxes already produce it, and a row whose subtitle is
 * absent closes up instead of leaving a gap.
 *
 * `last:border-b-0` because the card's own border draws that edge — both would
 * be a two-pixel rule under the final row.
 */
export const PeopleRow: FC<PeopleRowProps> = ({ title, subtitle, badge, trailing }) => (
  <tr className="border-b border-[var(--ds-color-border-primary)] last:border-b-0">
    <td className={CELL_TITLE}>
      {/* truncate, because table-fixed cells cannot grow for a long name and an
          email is easily wider than a third of the table. */}
      <div className="truncate text-[14px] leading-[20px] font-medium text-[var(--ds-color-text-primary)]">
        {title}
      </div>
      {subtitle !== undefined && (
        <div className="truncate text-[13px] leading-[18px] text-[var(--ds-color-raw-stone-600)]">
          {subtitle}
        </div>
      )}
    </td>
    <td className={CELL_BADGE}>{badge}</td>
    <td className={`${CELL_TRAILING} ${TRAILING_TEXT}`}>{trailing}</td>
  </tr>
);

/**
 * Placeholder rows, rendered inside the table rather than in place of it: the
 * header row and the card are already correct before the response lands, so
 * only the rows need standing in for and nothing moves when they arrive.
 *
 * Bars sit inside line boxes of 20 and 18 — the row's own two lines — so the
 * line boxes set the height and the bar thickness stays free of it.
 */
export const PeopleTableSkeletonRows: FC = () => (
  <>
    {Array.from({ length: PLACEHOLDER_COUNT }, (_, index) => (
      <tr
        className="border-b border-[var(--ds-color-border-primary)] last:border-b-0"
        data-testid="people-row-placeholder"
        key={index}
      >
        <td className={CELL_TITLE}>
          <div className="flex h-5 items-center">
            <div className={`h-3 w-2/5 rounded-full ${FILL} ${PULSE}`} />
          </div>
          <div className="flex h-[18px] items-center">
            <div className={`h-2 w-3/5 rounded-full ${FILL} ${PULSE}`} />
          </div>
        </td>
        <td className={CELL_BADGE}>
          {/* The badge's own 20px min-height, so the pill does not appear to
              grow when the real one replaces this. */}
          <div className={`h-5 w-12 rounded-full ${FILL} ${PULSE}`} />
        </td>
        <td className={CELL_TRAILING}>
          <div className="flex h-5 items-center">
            <div className={`h-3 w-24 rounded-full ${FILL} ${PULSE}`} />
          </div>
        </td>
      </tr>
    ))}
  </>
);
